/* eslint-disable react/jsx-no-useless-fragment */
import { useState, useRef, useEffect, KeyboardEvent, ChangeEvent } from "react";
import clsx from "clsx";
import SearchIcon from "@zenith/assets/icons/SearchIcon";

type SearchSize = "medium" | "large";

export type SearchOption = {
  label: string;
  value: string;
};

export type SearchDropdownState = {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  actionLabel?: string;
  actionIcon?: React.ReactNode;
  onAction?: () => void;
};

export type SearchProps = {
  size?: SearchSize;
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onSearch?: (value: string) => void;
  onClear?: () => void;
  options?: SearchOption[];
  disabled?: boolean;
  error?: boolean;
  noResultsText?: string;
  className?: string;
  isAutocomplete?: boolean;
  dropdownState?: SearchDropdownState | null;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
};

export function SearchComponent({
  size = "medium",
  label,
  placeholder = "Search",
  value,
  onChange,
  onSearch,
  onClear,
  options = [],
  disabled = false,
  error = false,
  noResultsText = "No results found",
  className,
  isAutocomplete = true,
  dropdownState = null,
  onKeyDown,
}: SearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(value.toLowerCase())
  );
  const hasResults = filtered.length > 0;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setIsOpen(false);
        setActiveIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    if (isAutocomplete && !disabled) setIsOpen(true);
  };

  const handleSelect = (option: SearchOption) => {
    onChange(option.label);
    setIsOpen(false);
    setActiveIndex(null);
    onSearch?.(option.label);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Only run autocomplete logic if enabled
    if (isAutocomplete && isOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) =>
          prev === null ? 0 : Math.min(prev + 1, filtered.length - 1)
        );
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((prev) => (prev === null ? 0 : Math.max(prev - 1, 0)));
      }

      if (e.key === "Enter") {
        if (activeIndex !== null && filtered[activeIndex]) {
          handleSelect(filtered[activeIndex]);
        } else {
          onSearch?.(value);
          setIsOpen(false);
        }
      }

      if (e.key === "Escape") setIsOpen(false);
    }

    onKeyDown?.(e);
  };

  const showClear = !!value && !disabled;
  const sizeStyles =
    size === "medium" ? "h-[40px] text-sm px-3" : "h-[48px] text-base px-4";
  const iconSize = size === "medium" ? "w-4 h-4" : "w-5 h-5";

  const inputStyles = clsx(
    "w-full rounded-lg border transition-all duration-150 outline-none placeholder-neutral-200", // <--- add this
    sizeStyles,
    disabled &&
      "bg-neutral-100 border-neutral-100 text-neutral-300 cursor-not-allowed",
    !disabled && error && "border-primary-300",
    !disabled && !error && "border-neutral-300 bg-white",
    !disabled &&
      "hover:border-neutral-400 focus:border-neutral-900 focus:ring-1 focus:ring-neutral-900"
  );

  return (
    <div ref={containerRef} className={clsx("relative", className)}>
      {/* LABEL */}
      {label && (
        <span
          className={clsx(
            "mb-1 block text-xs font-medium",
            disabled ? "text-neutral-200" : "text-neutral-600"
          )}
        >
          {label}
        </span>
      )}

      {/* INPUT */}
      <div className="relative">
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={() => !disabled && isAutocomplete && setIsOpen(true)}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className={clsx(inputStyles, "pr-10")}
        />

        {/* RIGHT SECTION */}
        <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-3">
          {/* CLEAR */}
          {showClear && (
            <button
              type="button"
              onClick={() => {
                onChange("");
                setIsOpen(false);
                onClear?.();
              }}
              className="text-neutral-600 hover:text-neutral-900"
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={iconSize}
              >
                <line x1="4" y1="4" x2="16" y2="16" />
                <line x1="16" y1="4" x2="4" y2="16" />
              </svg>
            </button>
          )}

          {/* DIVIDER */}
          <div
            className={clsx(
              "h-5 w-px",
              disabled ? "bg-neutral-200" : "bg-neutral-300"
            )}
          />

          {/* SEARCH ICON */}
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              if (!disabled) onSearch?.(value);
            }}
            className={clsx(
              "flex items-center justify-center",
              !disabled &&
                "cursor-pointer text-neutral-600 hover:text-neutral-900",
              disabled && "text-neutral-300"
            )}
          >
            <SearchIcon className={iconSize} />
          </button>
        </div>
      </div>

      {/* DROPDOWN */}
      {isAutocomplete && isOpen && !disabled && (
        <div className="absolute z-50 mt-2 max-h-60 w-full overflow-y-auto rounded-md bg-white shadow-lg">
          {/* EMPTY / ERROR STATE */}
          {value && !hasResults && (
            <>
              {dropdownState ? (
                <div className="flex w-full flex-col items-center justify-center p-12 text-center">
                  {dropdownState.icon && (
                    <span className="text-neutral-400">
                      {dropdownState.icon}
                    </span>
                  )}
                  <h4 className="text-sm font-medium text-neutral-400">
                    {dropdownState.title}
                  </h4>
                  {dropdownState.subtitle && (
                    <p className="mt-2 max-w-xs text-sm text-neutral-300">
                      {dropdownState.subtitle}
                    </p>
                  )}
                  {dropdownState.actionLabel && dropdownState.onAction && (
                    <button
                      onClick={dropdownState.onAction}
                      className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-primary-300 hover:underline"
                    >
                      {dropdownState.actionIcon}
                      {dropdownState.actionLabel}
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex w-full flex-col items-center justify-center gap-2 py-6 text-xs text-neutral-400">
                  <svg
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    className="h-4 w-4"
                  >
                    <circle cx="11" cy="11" r="8" />
                  </svg>
                  {noResultsText}
                </div>
              )}
            </>
          )}

          {/* OPTIONS */}
          {filtered.map((option, index) => (
            <div
              key={option.value}
              onClick={() => handleSelect(option)}
              className={clsx(
                "cursor-pointer px-4 py-2 text-sm",
                index === activeIndex ? "bg-neutral-100" : "hover:bg-neutral-50"
              )}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
